import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { ChevronRight, ChevronDown, Folder, File, Network, TreePine } from 'lucide-react'
import { useCrawlStructure, type StructureNode } from '../hooks/useCrawls'
import { scoreColor } from '@/client/lib/colors'
import { StructureGraph } from './StructureGraph'

interface StructureTabProps {
  crawlId: string
}

/** Collapsible tree node for the site structure view */
function TreeItem({ node, depth = 0 }: { node: StructureNode; depth?: number }) {
  const [expanded, setExpanded] = useState(depth < 2)
  const hasChildren = node.children.length > 0
  const isLeaf = !hasChildren

  const nodeScoreColor = scoreColor(node.avgSeoScore)

  return (
    <div>
      <button
        type="button"
        onClick={() => hasChildren && setExpanded(!expanded)}
        className={`flex items-center w-full text-left py-1.5 px-2 rounded hover:bg-muted/50 text-sm ${hasChildren ? 'cursor-pointer' : 'cursor-default'}`}
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
      >
        <span className="w-4 h-4 mr-1 flex-shrink-0">
          {hasChildren ? (
            expanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )
          ) : null}
        </span>
        {isLeaf ? (
          <File className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
        ) : (
          <Folder className="h-4 w-4 mr-2 text-blue-500 flex-shrink-0" />
        )}
        <span className="font-mono text-sm truncate mr-3 min-w-0 flex-1">
          {node.name === '/' ? '/ (root)' : node.name}
        </span>
        <span className="text-muted-foreground text-xs whitespace-nowrap mr-3">
          {node.pageCount} {node.pageCount === 1 ? 'page' : 'pages'}
        </span>
        {node.avgSeoScore > 0 && (
          <span className={`text-xs font-medium whitespace-nowrap mr-3 ${nodeScoreColor}`}>
            {node.avgSeoScore} SEO
          </span>
        )}
        <span className="text-muted-foreground text-xs whitespace-nowrap">
          {node.avgResponseTime}ms
        </span>
      </button>
      {expanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <TreeItem key={child.path} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  )
}

export function StructureTab({ crawlId }: StructureTabProps) {
  const [view, setView] = useState<'tree' | 'graph'>('tree')
  const { data, isLoading, error } = useCrawlStructure(crawlId, true)

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Failed to load site structure.
        </CardContent>
      </Card>
    )
  }

  const maxDepthCount = Math.max(...data.depthDistribution.map((d) => d.count), 1)

  return (
    <div className="space-y-4">
      {/* View Toggle */}
      <div className="flex items-center gap-1">
        <Button
          variant={view === 'tree' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setView('tree')}
        >
          <TreePine className="mr-1.5 h-3.5 w-3.5" />
          Tree View
        </Button>
        <Button
          variant={view === 'graph' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setView('graph')}
        >
          <Network className="mr-1.5 h-3.5 w-3.5" />
          Graph View
        </Button>
      </div>

      {/* Graph View */}
      {view === 'graph' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Internal Link Graph</CardTitle>
          </CardHeader>
          <CardContent>
            <StructureGraph crawlId={crawlId} />
          </CardContent>
        </Card>
      )}

      {/* Depth Distribution */}
      {view === 'tree' && (
        <>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Crawl Depth Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.depthDistribution.map((d) => {
                  const pct = (d.count / maxDepthCount) * 100
                  const isDeep = d.depth >= 4
                  return (
                    <div key={d.depth} className="flex items-center gap-3 text-sm">
                      <span className="w-20 text-muted-foreground whitespace-nowrap">
                        Depth {d.depth}{d.depth === 0 ? ' (root)' : ''}
                      </span>
                      <div className="flex-1 h-5 bg-muted rounded overflow-hidden">
                        <div
                          className={`h-full rounded transition-all ${isDeep ? 'bg-red-500/70' : 'bg-primary/70'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className={`w-16 text-right font-mono text-xs ${isDeep ? 'text-red-600 dark:text-red-400' : ''}`}>
                        {d.count} {d.count === 1 ? 'page' : 'pages'}
                      </span>
                    </div>
                  )
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                {data.totalPages} total pages. Pages at depth 4+ are harder for search engines to discover.
              </p>
            </CardContent>
          </Card>

          {/* Directory Tree */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Site Structure</CardTitle>
            </CardHeader>
            <CardContent className="p-0 pb-2">
              <div className="max-h-[600px] overflow-y-auto">
                <TreeItem node={data.tree} />
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
